'use client'

import { cn } from '@tebuto/lib/utils'
import type { Editor } from '@tiptap/react'
import { Plus, Table2, Trash2 } from 'lucide-react'
import { type JSX, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const DEFAULT_TABLE_CONFIG = {
    rows: 3,
    cols: 3,
    withHeaderRow: true
} as const

const TABLE_OVERLAY_ATTRIBUTE = 'data-table-overlay'
const SEPARATOR_HOVER_THRESHOLD_PX = 8
const COLUMN_BUTTON_OFFSET_PX = 13
const ROW_BUTTON_OFFSET_PX = 13

interface EditorTableControlsProps {
    editor: Editor | null
    readOnly?: boolean
    dataTestIdPrefix?: string
}

type TableAction = {
    key: string
    label: string
    disabled: boolean
    onSelect: () => void
}

type OverlayRect = {
    top: number
    left: number
    width: number
    height: number
    right: number
    bottom: number
}

type HoveredCellLayout = {
    cellElement: HTMLElement
    cellRect: OverlayRect
    rowRect: OverlayRect
    tableRect: OverlayRect
}

type SeparatorOverlay = {
    action: TableAction
    lineRect: OverlayRect
    buttonTop: number
    buttonLeft: number
}

type DeleteTriggerOverlay = {
    action: TableAction
    buttonTop: number
    buttonLeft: number
}

function useTableActions({
    editor,
    readOnly = false
}: Pick<EditorTableControlsProps, 'editor' | 'readOnly'>): Record<string, TableAction> {
    const isTableActive = editor?.isActive('table') ?? false

    return useMemo<Record<string, TableAction>>(() => {
        const canEdit = !readOnly && editor !== null

        return {
            addColumnBefore: {
                key: 'add-column-before',
                label: 'Spalte davor',
                disabled: !canEdit || !isTableActive || !(editor?.can().addColumnBefore() ?? false),
                onSelect: () => {
                    editor?.chain().focus(undefined, { scrollIntoView: false }).addColumnBefore().run()
                }
            },
            addColumnAfter: {
                key: 'add-column-after',
                label: 'Spalte danach',
                disabled: !canEdit || !isTableActive || !(editor?.can().addColumnAfter() ?? false),
                onSelect: () => {
                    editor?.chain().focus(undefined, { scrollIntoView: false }).addColumnAfter().run()
                }
            },
            addRowBefore: {
                key: 'add-row-before',
                label: 'Zeile darüber',
                disabled: !canEdit || !isTableActive || !(editor?.can().addRowBefore() ?? false),
                onSelect: () => {
                    editor?.chain().focus(undefined, { scrollIntoView: false }).addRowBefore().run()
                }
            },
            addRowAfter: {
                key: 'add-row-after',
                label: 'Zeile darunter',
                disabled: !canEdit || !isTableActive || !(editor?.can().addRowAfter() ?? false),
                onSelect: () => {
                    editor?.chain().focus(undefined, { scrollIntoView: false }).addRowAfter().run()
                }
            },
            deleteColumn: {
                key: 'delete-column',
                label: 'Spalte löschen',
                disabled: !canEdit || !isTableActive || !(editor?.can().deleteColumn() ?? false),
                onSelect: () => {
                    editor?.chain().focus(undefined, { scrollIntoView: false }).deleteColumn().run()
                }
            },
            deleteRow: {
                key: 'delete-row',
                label: 'Zeile löschen',
                disabled: !canEdit || !isTableActive || !(editor?.can().deleteRow() ?? false),
                onSelect: () => {
                    editor?.chain().focus(undefined, { scrollIntoView: false }).deleteRow().run()
                }
            }
        }
    }, [editor, isTableActive, readOnly])
}

export function InsertTableButton({
    editor,
    readOnly = false,
    dataTestIdPrefix
}: EditorTableControlsProps): JSX.Element {
    const [isInsideTable, setIsInsideTable] = useState(editor?.isActive('table') ?? false)

    useEffect(() => {
        if (!editor) {
            setIsInsideTable(false)
            return
        }

        const updateIsInsideTable = () => {
            setIsInsideTable(editor.isActive('table'))
        }

        updateIsInsideTable()

        editor.on('selectionUpdate', updateIsInsideTable)
        editor.on('update', updateIsInsideTable)
        editor.on('focus', updateIsInsideTable)
        editor.on('blur', updateIsInsideTable)

        return () => {
            editor.off('selectionUpdate', updateIsInsideTable)
            editor.off('update', updateIsInsideTable)
            editor.off('focus', updateIsInsideTable)
            editor.off('blur', updateIsInsideTable)
        }
    }, [editor])

    const isDisabled = readOnly || editor === null || isInsideTable

    return (
        <button
            type="button"
            tabIndex={-1}
            disabled={isDisabled}
            className={cn(
                'inline-flex h-8 items-center justify-center rounded-md px-2 text-gray-500 transition-colors',
                'hover:bg-gray-100 hover:text-gray-700',
                isDisabled && 'cursor-not-allowed opacity-50 hover:bg-transparent hover:text-gray-500'
            )}
            data-testid={dataTestIdPrefix ? `${dataTestIdPrefix}-table-trigger` : undefined}
            aria-label="Tabelle einfügen"
            title={isInsideTable ? 'Tabelle innerhalb einer Tabelle ist nicht erlaubt' : 'Tabelle einfügen'}
            onClick={() => {
                editor?.chain().focus(undefined, { scrollIntoView: false }).insertTable(DEFAULT_TABLE_CONFIG).run()
            }}
        >
            <Table2 className="h-4 w-4" />
        </button>
    )
}

export function EditorTableBubbleMenu({
    editor,
    readOnly = false,
    dataTestIdPrefix
}: EditorTableControlsProps): JSX.Element | null {
    const actions = useTableActions({ editor, readOnly })
    const [hoveredCellLayout, setHoveredCellLayout] = useState<HoveredCellLayout | null>(null)
    const [separatorOverlay, setSeparatorOverlay] = useState<SeparatorOverlay | null>(null)
    const [isPointerInsideTable, setIsPointerInsideTable] = useState(false)
    const [isPointerInsideOverlay, setIsPointerInsideOverlay] = useState(false)
    const lastPointerRef = useRef<{ x: number; y: number } | null>(null)

    useEffect(() => {
        if (!editor || readOnly) {
            setHoveredCellLayout(null)
            setSeparatorOverlay(null)
            setIsPointerInsideTable(false)
            setIsPointerInsideOverlay(false)
            return
        }

        const updateHoveredLayout = () => {
            setHoveredCellLayout((currentLayout) => {
                if (!currentLayout?.cellElement.isConnected) {
                    setSeparatorOverlay(null)
                    return null
                }

                const nextLayout = getHoveredCellLayout(currentLayout.cellElement)

                if (!nextLayout) {
                    setSeparatorOverlay(null)
                    return null
                }

                if (lastPointerRef.current) {
                    setSeparatorOverlay(
                        getSeparatorOverlay(nextLayout, lastPointerRef.current.x, lastPointerRef.current.y, actions)
                    )
                }

                return nextLayout
            })
        }

        const handleMouseMove = (event: MouseEvent) => {
            const cellElement = getClosestTableCell(event.target)

            if (!cellElement) {
                setIsPointerInsideTable(false)
                if (!isPointerInsideOverlay) {
                    setHoveredCellLayout(null)
                    setSeparatorOverlay(null)
                }
                return
            }

            const nextLayout = getHoveredCellLayout(cellElement)

            if (!nextLayout) {
                setIsPointerInsideTable(false)
                setHoveredCellLayout(null)
                setSeparatorOverlay(null)
                return
            }

            lastPointerRef.current = { x: event.clientX, y: event.clientY }
            setHoveredCellLayout(nextLayout)
            setSeparatorOverlay(getSeparatorOverlay(nextLayout, event.clientX, event.clientY, actions))
            setIsPointerInsideTable(true)
        }

        const handleMouseLeave = (event: MouseEvent) => {
            const nextCellElement = getClosestTableCell(event.relatedTarget)

            if (nextCellElement || isTableOverlayElement(event.relatedTarget)) {
                return
            }

            setIsPointerInsideTable(false)
            setHoveredCellLayout(null)
            setSeparatorOverlay(null)
        }

        editor.view.dom.addEventListener('mousemove', handleMouseMove)
        editor.view.dom.addEventListener('mouseleave', handleMouseLeave)
        editor.on('selectionUpdate', updateHoveredLayout)
        editor.on('update', updateHoveredLayout)
        window.addEventListener('resize', updateHoveredLayout)
        window.addEventListener('scroll', updateHoveredLayout, true)

        return () => {
            editor.view.dom.removeEventListener('mousemove', handleMouseMove)
            editor.view.dom.removeEventListener('mouseleave', handleMouseLeave)
            editor.off('selectionUpdate', updateHoveredLayout)
            editor.off('update', updateHoveredLayout)
            window.removeEventListener('resize', updateHoveredLayout)
            window.removeEventListener('scroll', updateHoveredLayout, true)
        }
    }, [actions, editor, isPointerInsideOverlay, readOnly])

    if (!editor || readOnly || !hoveredCellLayout) {
        return null
    }

    const deleteColumnTrigger = getDeleteColumnTrigger(hoveredCellLayout, actions.deleteColumn)
    const deleteRowTrigger = getDeleteRowTrigger(hoveredCellLayout, actions.deleteRow)
    const showOverlays = isPointerInsideTable || isPointerInsideOverlay

    return createPortal(
        <>
            {showOverlays && separatorOverlay && (
                <>
                    <div
                        {...{ [TABLE_OVERLAY_ATTRIBUTE]: 'true' }}
                        className="pointer-events-none fixed z-50 bg-primary-500/40"
                        style={{
                            top: separatorOverlay.lineRect.top,
                            left: separatorOverlay.lineRect.left,
                            width: separatorOverlay.lineRect.width,
                            height: separatorOverlay.lineRect.height
                        }}
                    />
                    <button
                        type="button"
                        {...{ [TABLE_OVERLAY_ATTRIBUTE]: 'true' }}
                        className="fixed z-50 inline-flex size-6 items-center justify-center rounded-full border border-primary-200 bg-white text-primary-600 shadow-md transition-colors hover:bg-primary-50"
                        style={{
                            top: separatorOverlay.buttonTop,
                            left: separatorOverlay.buttonLeft
                        }}
                        data-testid={
                            dataTestIdPrefix ? `${dataTestIdPrefix}-table-${separatorOverlay.action.key}` : undefined
                        }
                        title={separatorOverlay.action.label}
                        disabled={separatorOverlay.action.disabled}
                        onMouseEnter={() => {
                            setIsPointerInsideOverlay(true)
                        }}
                        onMouseLeave={() => {
                            setIsPointerInsideOverlay(false)
                            if (!isPointerInsideTable) {
                                setHoveredCellLayout(null)
                                setSeparatorOverlay(null)
                            }
                        }}
                        onMouseDown={(event) => {
                            event.preventDefault()
                        }}
                        onClick={() => {
                            separatorOverlay.action.onSelect()
                        }}
                    >
                        <Plus className="size-3.5" />
                    </button>
                </>
            )}

            {showOverlays && (
                <button
                    type="button"
                    {...{ [TABLE_OVERLAY_ATTRIBUTE]: 'true' }}
                    className="fixed z-50 inline-flex size-6 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition-colors hover:bg-red-50 hover:text-red-600"
                    style={{
                        top: deleteColumnTrigger.buttonTop,
                        left: deleteColumnTrigger.buttonLeft
                    }}
                    data-testid={dataTestIdPrefix ? `${dataTestIdPrefix}-table-delete-column` : undefined}
                    title={actions.deleteColumn.label}
                    disabled={actions.deleteColumn.disabled}
                    onMouseEnter={() => {
                        setIsPointerInsideOverlay(true)
                    }}
                    onMouseLeave={() => {
                        setIsPointerInsideOverlay(false)
                        if (!isPointerInsideTable) {
                            setHoveredCellLayout(null)
                            setSeparatorOverlay(null)
                        }
                    }}
                    onMouseDown={(event) => {
                        event.preventDefault()
                    }}
                    onClick={() => {
                        actions.deleteColumn.onSelect()
                    }}
                >
                    <Trash2 className="size-3.5" />
                </button>
            )}

            {showOverlays && (
                <button
                    type="button"
                    {...{ [TABLE_OVERLAY_ATTRIBUTE]: 'true' }}
                    className="fixed z-50 inline-flex size-6 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-500 shadow-sm transition-colors hover:bg-red-50 hover:text-red-600"
                    style={{
                        top: deleteRowTrigger.buttonTop,
                        left: deleteRowTrigger.buttonLeft
                    }}
                    data-testid={dataTestIdPrefix ? `${dataTestIdPrefix}-table-delete-row` : undefined}
                    title={actions.deleteRow.label}
                    disabled={actions.deleteRow.disabled}
                    onMouseEnter={() => {
                        setIsPointerInsideOverlay(true)
                    }}
                    onMouseLeave={() => {
                        setIsPointerInsideOverlay(false)
                        if (!isPointerInsideTable) {
                            setHoveredCellLayout(null)
                            setSeparatorOverlay(null)
                        }
                    }}
                    onMouseDown={(event) => {
                        event.preventDefault()
                    }}
                    onClick={() => {
                        actions.deleteRow.onSelect()
                    }}
                >
                    <Trash2 className="size-3.5" />
                </button>
            )}
        </>,
        document.body
    )
}

function getClosestTableCell(target: EventTarget | null): HTMLElement | null {
    if (!(target instanceof HTMLElement)) {
        return null
    }

    const cellElement = target.closest('td, th')

    return cellElement instanceof HTMLElement ? cellElement : null
}

function getHoveredCellLayout(cellElement: HTMLElement): HoveredCellLayout | null {
    const rowElement = cellElement.closest('tr')
    const tableElement = cellElement.closest('table')

    if (!(rowElement instanceof HTMLElement) || !(tableElement instanceof HTMLElement)) {
        return null
    }

    return {
        cellElement,
        cellRect: toOverlayRect(cellElement.getBoundingClientRect()),
        rowRect: toOverlayRect(rowElement.getBoundingClientRect()),
        tableRect: toOverlayRect(tableElement.getBoundingClientRect())
    }
}

function getSeparatorOverlay(
    layout: HoveredCellLayout,
    pointerX: number,
    pointerY: number,
    actions: Record<string, TableAction>
): SeparatorOverlay | null {
    const candidates: Array<{ distance: number; overlay: SeparatorOverlay }> = []

    const distanceToLeft = Math.abs(pointerX - layout.cellRect.left)
    if (distanceToLeft <= SEPARATOR_HOVER_THRESHOLD_PX) {
        candidates.push({
            distance: distanceToLeft,
            overlay: {
                action: actions.addColumnBefore,
                lineRect: {
                    top: layout.tableRect.top,
                    left: layout.cellRect.left,
                    width: 1,
                    height: layout.tableRect.height,
                    right: layout.cellRect.left + 1,
                    bottom: layout.tableRect.bottom
                },
                buttonTop: layout.tableRect.top - COLUMN_BUTTON_OFFSET_PX,
                buttonLeft: layout.cellRect.left - COLUMN_BUTTON_OFFSET_PX
            }
        })
    }

    const distanceToRight = Math.abs(pointerX - layout.cellRect.right)
    if (distanceToRight <= SEPARATOR_HOVER_THRESHOLD_PX) {
        candidates.push({
            distance: distanceToRight,
            overlay: {
                action: actions.addColumnAfter,
                lineRect: {
                    top: layout.tableRect.top,
                    left: layout.cellRect.right,
                    width: 1,
                    height: layout.tableRect.height,
                    right: layout.cellRect.right + 1,
                    bottom: layout.tableRect.bottom
                },
                buttonTop: layout.tableRect.top - COLUMN_BUTTON_OFFSET_PX,
                buttonLeft: layout.cellRect.right - COLUMN_BUTTON_OFFSET_PX
            }
        })
    }

    const distanceToTop = Math.abs(pointerY - layout.cellRect.top)
    if (distanceToTop <= SEPARATOR_HOVER_THRESHOLD_PX) {
        candidates.push({
            distance: distanceToTop,
            overlay: {
                action: actions.addRowBefore,
                lineRect: {
                    top: layout.cellRect.top,
                    left: layout.tableRect.left,
                    width: layout.tableRect.width,
                    height: 1,
                    right: layout.tableRect.right,
                    bottom: layout.cellRect.top + 1
                },
                buttonTop: layout.cellRect.top - ROW_BUTTON_OFFSET_PX,
                buttonLeft: layout.tableRect.left - ROW_BUTTON_OFFSET_PX
            }
        })
    }

    const distanceToBottom = Math.abs(pointerY - layout.cellRect.bottom)
    if (distanceToBottom <= SEPARATOR_HOVER_THRESHOLD_PX) {
        candidates.push({
            distance: distanceToBottom,
            overlay: {
                action: actions.addRowAfter,
                lineRect: {
                    top: layout.cellRect.bottom,
                    left: layout.tableRect.left,
                    width: layout.tableRect.width,
                    height: 1,
                    right: layout.tableRect.right,
                    bottom: layout.cellRect.bottom + 1
                },
                buttonTop: layout.cellRect.bottom - ROW_BUTTON_OFFSET_PX,
                buttonLeft: layout.tableRect.left - ROW_BUTTON_OFFSET_PX
            }
        })
    }

    if (candidates.length === 0) {
        return null
    }

    candidates.sort((leftCandidate, rightCandidate) => leftCandidate.distance - rightCandidate.distance)

    return candidates[0].overlay
}

function getDeleteColumnTrigger(layout: HoveredCellLayout, action: TableAction): DeleteTriggerOverlay {
    return {
        action,
        buttonTop: layout.tableRect.top - COLUMN_BUTTON_OFFSET_PX,
        buttonLeft: layout.cellRect.left + layout.cellRect.width / 2 - COLUMN_BUTTON_OFFSET_PX
    }
}

function getDeleteRowTrigger(layout: HoveredCellLayout, action: TableAction): DeleteTriggerOverlay {
    return {
        action,
        buttonTop: layout.rowRect.top + layout.rowRect.height / 2 - ROW_BUTTON_OFFSET_PX,
        buttonLeft: layout.tableRect.right - ROW_BUTTON_OFFSET_PX
    }
}

function toOverlayRect(rect: DOMRect): OverlayRect {
    return {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        right: rect.right,
        bottom: rect.bottom
    }
}

function isTableOverlayElement(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
        return false
    }

    return target.closest(`[${TABLE_OVERLAY_ATTRIBUTE}="true"]`) !== null
}
